import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';

const DEFAULT_PAGE_SIZE = 20;
const HIDDEN_COLUMNS = new Set(['ema50', 'ema200', 'adx', 'atr']);
const COLUMN_LABELS = {
  tp2Percent: 'tp2 %',
  tp1Percent: 'tp1 %',
  tp1Price: 'tp1',
  tp2Price: 'tp2',
  exitReason: 'Exit',
  totalUsdt: 'Total USDT',
};

const reorderColumns = (cols) => {
  const out = [...cols];
  const pnlKey = 'pnlUsdt';
  const exitKey = 'exitReason';
  const entryKey = 'entryPrice';

  if (!out.includes(pnlKey) || !out.includes(exitKey) || !out.includes(entryKey)) {
    return out;
  }

  // Move pnlUsdt to be right after exitReason (i.e., between Exit and entryPrice)
  const pnlIdx = out.indexOf(pnlKey);
  out.splice(pnlIdx, 1);
  const exitIdx = out.indexOf(exitKey);
  out.splice(exitIdx + 1, 0, pnlKey);
  return out;
};

const PaginatedTable = ({ rows, formatCellValue, pageSize = DEFAULT_PAGE_SIZE }) => {
  const [page, setPage] = useState(1);

  const safeRows = Array.isArray(rows) ? rows : [];
  const totalRows = safeRows.length;

  const columns = useMemo(() => {
    const allColumns = Object.keys(safeRows[0] || {});
    const filtered = allColumns.filter((c) => !HIDDEN_COLUMNS.has(c));
    return reorderColumns(filtered);
  }, [safeRows]);

  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [page, pageCount]);

  if (columns.length === 0) return null;
  if (totalRows === 0) return null;

  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(totalRows, startIdx + pageSize);
  const pageRows = safeRows.slice(startIdx, endIdx);

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-dark-700">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white"
                >
                  {COLUMN_LABELS[col] || col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, idx) => (
              <tr key={startIdx + idx} className="border-b border-dark-700">
                {columns.map((col) => {
                  const value = row[col];
                  const isTrendCol = col.toLowerCase() === 'trend';

                  if (isTrendCol) {
                    const normalized = String(value ?? '').toUpperCase();
                    const badgeClass =
                      normalized === 'UP'
                        ? 'bg-green-500 text-white'
                        : normalized === 'DOWN'
                        ? 'bg-red-500 text-white'
                        : 'bg-dark-700 text-dark-200';

                    return (
                      <td
                        key={col}
                        className="px-4 py-3 text-xs sm:text-sm text-dark-200"
                      >
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeClass}`}
                        >
                          {normalized || '-'}
                        </span>
                      </td>
                    );
                  }

                  // If the value is a React element (e.g., actions button), render directly.
                  if (React.isValidElement(value)) {
                    return (
                      <td key={col} className="px-4 py-3 text-xs sm:text-sm text-dark-200">
                        {value}
                      </td>
                    );
                  }

                  if (col === 'pnlUsdt') {
                    const n = Number(value);
                    const colorClass = Number.isNaN(n)
                      ? 'text-dark-200'
                      : n >= 0
                      ? 'text-green-400'
                      : 'text-red-400';

                    return (
                      <td
                        key={col}
                        className="px-4 py-3 text-xs sm:text-sm"
                      >
                        <span className={colorClass}>
                          {formatCellValue(col, value)}
                        </span>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={col}
                      className="px-4 py-3 text-xs sm:text-sm text-dark-200"
                    >
                      {formatCellValue(col, value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs sm:text-sm text-dark-400">
            Showing {startIdx + 1}-{endIdx} of {totalRows}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-xs sm:text-sm hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <div className="text-xs sm:text-sm text-dark-300">
              Page {page} / {pageCount}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-xs sm:text-sm hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Technicals = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('trending'); // trending | screener | currentTrending | deletedTrending | backtest | backtestDoub | blacklisted | symbolInfo

  // Shared snapshot inputs
  const [snapshotDate, setSnapshotDate] = useState('');
  const [snapshotHour, setSnapshotHour] = useState('');
  const [snapshotError, setSnapshotError] = useState('');

  // TrendingSymbolScreener state
  const [trendingRows, setTrendingRows] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [trendingError, setTrendingError] = useState('');

  // SymbolScreener state
  const [screenerRows, setScreenerRows] = useState([]);
  const [screenerLoading, setScreenerLoading] = useState(false);
  const [screenerError, setScreenerError] = useState('');

  // Daily trending state (current & deleted)
  const [currentTrendingRows, setCurrentTrendingRows] = useState([]);
  const [currentTrendingLoading, setCurrentTrendingLoading] = useState(false);
  const [currentTrendingError, setCurrentTrendingError] = useState('');
  const [currentTrendingDeleting, setCurrentTrendingDeleting] = useState(false);
  const [currentTrendingRanking, setCurrentTrendingRanking] = useState(false);
  const [currentTrendingSortColumn, setCurrentTrendingSortColumn] = useState('');
  const [currentTrendingSortDirection, setCurrentTrendingSortDirection] = useState('asc');

  const [deletedTrendingRows, setDeletedTrendingRows] = useState([]);
  const [deletedTrendingLoading, setDeletedTrendingLoading] = useState(false);
  const [deletedTrendingError, setDeletedTrendingError] = useState('');
  const [deletedDate, setDeletedDate] = useState('');

  // Backtest state
  const [backtestConfig, setBacktestConfig] = useState({
    symbol: 'BTCUSDT',
    daysBack: 30,
    timeFrame: '1h',
    samplingPeriod: 5,
    rangeMultiplier: 2.0,
    initialSizeUsdt: 100.0,
    tp1Percent: 0.8,
    tp2Percent: 1.6,
    slPercent: 1.0,
    waitFailures: 3,
    verbose: false,
  });
  const [backtestResult, setBacktestResult] = useState(null);
  const [backtestTrades, setBacktestTrades] = useState([]);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestError, setBacktestError] = useState('');

  // Backtest DOUB state
  const [backtestDoubConfig, setBacktestDoubConfig] = useState({
    symbol: 'BTCUSDT',
    daysBack: 30,
    timeFrame: '1h',
    samplingPeriod: 5,
    rangeMultiplier: 2.0,
    initialSizeUsdt: 100.0,
    tp1Percent: 0.8,
    tp2Percent: 1.6,
    slPercent: 1.0,
    waitFailures: 3,
    verbose: false,
  });
  const [backtestDoubResult, setBacktestDoubResult] = useState(null);
  const [backtestDoubTrades, setBacktestDoubTrades] = useState([]);
  const [backtestDoubLoading, setBacktestDoubLoading] = useState(false);
  const [backtestDoubError, setBacktestDoubError] = useState('');

  // Blacklisted symbols
  const [blacklistRows, setBlacklistRows] = useState([]);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [blacklistError, setBlacklistError] = useState('');
  const [blacklistSymbol, setBlacklistSymbol] = useState('');
  const [blacklistReason, setBlacklistReason] = useState('');

  // Symbol info
  const [symbolInfoSymbol, setSymbolInfoSymbol] = useState('');
  const [symbolInfoData, setSymbolInfoData] = useState(null);
  const [symbolInfoLoading, setSymbolInfoLoading] = useState(false);
  const [symbolInfoError, setSymbolInfoError] = useState('');
  const [symbolMarketData, setSymbolMarketData] = useState(null);
  const [symbolMarketLoading, setSymbolMarketLoading] = useState(false);
  const [symbolMarketError, setSymbolMarketError] = useState('');

  // Trending filters & sorting
  const [trendingSymbolFilter, setTrendingSymbolFilter] = useState('');
  const [trendingMinQuoteVol, setTrendingMinQuoteVol] = useState('');
  const [trendingMinPctChange, setTrendingMinPctChange] = useState('');
  const [trendingMinOpenInterest, setTrendingMinOpenInterest] = useState('');
  const [trendingMaxSpreadBps, setTrendingMaxSpreadBps] = useState('');
  const [trendingMinFundingRate, setTrendingMinFundingRate] = useState('');
  const [trendingMinScore, setTrendingMinScore] = useState('');
  const [trendingSortColumn, setTrendingSortColumn] = useState('score');
  const [trendingSortDirection, setTrendingSortDirection] = useState('desc');

  // Screener filters & sorting
  const [screenerSymbolFilter, setScreenerSymbolFilter] = useState('');
  const [screenerMinQuoteVol, setScreenerMinQuoteVol] = useState('');
  const [screenerMinPctChange, setScreenerMinPctChange] = useState('');
  const [screenerMinOpenInterest, setScreenerMinOpenInterest] = useState('');
  const [screenerMaxSpreadBps, setScreenerMaxSpreadBps] = useState('');
  const [screenerMinFundingRate, setScreenerMinFundingRate] = useState('');
  const [screenerMinScore, setScreenerMinScore] = useState('');
  const [screenerSortColumn, setScreenerSortColumn] = useState('score');
  const [screenerSortDirection, setScreenerSortDirection] = useState('desc');

  const hourOptions = Array.from({ length: 24 }, (_, i) => i);

  const parseNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  };

  const formatCellValue = (col, value) => {
    if (value == null) return '-';

    // Backtest numeric fields: show 5 decimal places
    if (
      col === 'entryPrice' ||
      col === 'tp1Percent' ||
      col === 'tp1Price' ||
      col === 'tp2Price' ||
      col === 'slPrice' ||
      col === 'pnlUsdt'
    ) {
      const n = Number(value);
      if (!Number.isNaN(n)) {
        return n.toFixed(5);
      }
    }

    if (col === 'runTime') {
      const s = String(value);
      const tIndex = s.indexOf('T');
      if (tIndex > 0) {
        const datePart = s.slice(0, tIndex);
        // Always show the selected snapshot hour, not the raw UTC hour from runTime
        if (datePart && snapshotHour !== '') {
          const hourStr = String(snapshotHour).padStart(2, '0');
          return `${datePart} ${hourStr} hour`;
        }
        return datePart;
      }
      return s;
    }

    return String(value);
  };

  const filterAndSortRows = (rows, {
    symbolFilter,
    minQuoteVol,
    minPctChange,
    minOpenInterest,
    maxSpreadBps,
    minFundingRate,
    minScore,
    sortColumn,
    sortDirection,
  }) => {
    if (!rows || rows.length === 0) return [];

    let result = rows.filter((row) => {
      if (symbolFilter) {
        const sym = String(row.symbol || '').toUpperCase();
        if (!sym.includes(symbolFilter.toUpperCase())) return false;
      }

      const qv = parseNumber(row.quoteVol);
      const qvMin = parseNumber(minQuoteVol);
      if (qvMin !== null && qv !== null && qv < qvMin) return false;

      const pc = parseNumber(row.pctChange);
      const pcMin = parseNumber(minPctChange);
      if (pcMin !== null && pc !== null && pc < pcMin) return false;

      const oi = parseNumber(row.openInterest);
      const oiMin = parseNumber(minOpenInterest);
      if (oiMin !== null && oi !== null && oi < oiMin) return false;

      const sb = parseNumber(row.spreadBps);
      const sbMax = parseNumber(maxSpreadBps);
      if (sbMax !== null && sb !== null && sb > sbMax) return false;

      const fr = parseNumber(row.fundingRate);
      const frMin = parseNumber(minFundingRate);
      if (frMin !== null && fr !== null && fr < frMin) return false;

      const sc = parseNumber(row.score);
      const scMin = parseNumber(minScore);
      if (scMin !== null && sc !== null && sc < scMin) return false;

      return true;
    });

    if (sortColumn) {
      const col = sortColumn;
      const dir = sortDirection === 'desc' ? -1 : 1;
      result = [...result].sort((a, b) => {
        const av = a[col];
        const bv = b[col];
        const an = parseNumber(av);
        const bn = parseNumber(bv);

        if (an !== null && bn !== null) {
          if (an === bn) return 0;
          return an < bn ? -1 * dir : 1 * dir;
        }

        const as = String(av ?? '');
        const bs = String(bv ?? '');
        const cmp = as.localeCompare(bs);
        return cmp * dir;
      });
    }

    return result;
  };

  const renderGenericTable = (rows) => (
    <PaginatedTable rows={rows} formatCellValue={formatCellValue} pageSize={20} />
  );

  const handleLoadSnapshots = async () => {
    if (!snapshotDate || snapshotHour === '') {
      setSnapshotError('Please select both date and hour');
      return;
    }

    setSnapshotError('');
    setTrendingError('');
    setScreenerError('');
    setTrendingLoading(true);
    setScreenerLoading(true);

    try {
      const [trRes, scRes] = await Promise.allSettled([
        apiService.getTrendingSymbolSnapshot({
          date: snapshotDate,
          hour: Number(snapshotHour),
        }),
        apiService.getSymbolScreenerSnapshot({
          date: snapshotDate,
          hour: Number(snapshotHour),
        }),
      ]);

      if (trRes.status === 'fulfilled') {
        const res = trRes.value;
        const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setTrendingRows(data);
      } else {
        setTrendingError('Failed to load trending symbol snapshot');
        setTrendingRows([]);
      }

      if (scRes.status === 'fulfilled') {
        const res = scRes.value;
        const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setScreenerRows(data);
      } else {
        setScreenerError('Failed to load symbol screener snapshot');
        setScreenerRows([]);
      }
    } finally {
      setTrendingLoading(false);
      setScreenerLoading(false);
    }
  };

  const handleLoadCurrentTrending = async () => {
    setCurrentTrendingLoading(true);
    setCurrentTrendingError('');
    try {
      const res = await apiService.getCurrentTrending();
      const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setCurrentTrendingRows(data);
    } catch (e) {
      setCurrentTrendingError('Failed to load current trending data');
      setCurrentTrendingRows([]);
    } finally {
      setCurrentTrendingLoading(false);
    }
  };

  const handleRerankCurrentTrending = async () => {
    setCurrentTrendingRanking(true);
    setCurrentTrendingError('');
    try {
      await apiService.triggerCurrentTrendingRanking();
      await handleLoadCurrentTrending();
    } catch (e) {
      console.error('Failed to re-rank current trending symbols', e);
      setCurrentTrendingError('Failed to re-rank current trending symbols');
    } finally {
      setCurrentTrendingRanking(false);
    }
  };

  const sortCurrentTrendingRows = (rows) => {
    if (!currentTrendingSortColumn) return rows;
    const dir = currentTrendingSortDirection === 'desc' ? -1 : 1;
    const numCols = new Set(['currentTrendingSymbolId', 'rank', 'volatilityScore']);
    const dateCols = new Set(['startTime', 'endTime']);

    return [...rows].sort((a, b) => {
      const av = a?.[currentTrendingSortColumn];
      const bv = b?.[currentTrendingSortColumn];

      if (numCols.has(currentTrendingSortColumn)) {
        const an = Number(av);
        const bn = Number(bv);
        if (Number.isNaN(an) && Number.isNaN(bn)) return 0;
        if (Number.isNaN(an)) return -1 * dir;
        if (Number.isNaN(bn)) return 1 * dir;
        if (an === bn) return 0;
        return an < bn ? -1 * dir : 1 * dir;
      }

      if (dateCols.has(currentTrendingSortColumn)) {
        const ad = av ? new Date(av).getTime() : 0;
        const bd = bv ? new Date(bv).getTime() : 0;
        if (ad === bd) return 0;
        return ad < bd ? -1 * dir : 1 * dir;
      }

      const as = String(av ?? '');
      const bs = String(bv ?? '');
      const cmp = as.localeCompare(bs);
      return cmp * dir;
    });
  };

  const handleCurrentTrendingSort = (col) => {
    setCurrentTrendingSortColumn((prevCol) => {
      if (prevCol === col) {
        setCurrentTrendingSortDirection((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevCol;
      }
      setCurrentTrendingSortDirection('asc');
      return col;
    });
  };

  const handleDeleteCurrentTrending = async (symbol) => {
    if (!symbol) return;
    setCurrentTrendingDeleting(true);
    setCurrentTrendingError('');
    try {
      await apiService.deleteCurrentTrending(symbol);
      await handleLoadCurrentTrending();
    } catch (e) {
      console.error('Failed to delete current trending symbol', e);
      setCurrentTrendingError('Failed to delete symbol');
    } finally {
      setCurrentTrendingDeleting(false);
    }
  };

  const handleLoadDeletedTrending = async () => {
    if (!deletedDate) {
      setDeletedTrendingError('Please select a date');
      return;
    }
    setDeletedTrendingLoading(true);
    setDeletedTrendingError('');
    try {
      const res = await apiService.getDeletedTrending({ date: deletedDate });
      const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setDeletedTrendingRows(data);
    } catch (e) {
      setDeletedTrendingError('Failed to load deleted trending data');
      setDeletedTrendingRows([]);
    } finally {
      setDeletedTrendingLoading(false);
    }
  };

  const handleBacktestInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    setBacktestConfig((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'symbol' || name === 'timeFrame'
          ? value
          : value === ''
          ? ''
          : Number(value),
    }));
  };

  const handleBacktestDoubInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    setBacktestDoubConfig((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'symbol' || name === 'timeFrame'
          ? value
          : value === ''
          ? ''
          : Number(value),
    }));
  };

  const handleRunBacktest = async () => {
    setBacktestLoading(true);
    setBacktestError('');
    try {
      const res = await apiService.runBacktestFlow(backtestConfig);
      setBacktestResult(res);
      setBacktestTrades(Array.isArray(res?.trades) ? res.trades : []);
    } catch (e) {
      setBacktestError('Failed to run backtest');
      setBacktestResult(null);
      setBacktestTrades([]);
    } finally {
      setBacktestLoading(false);
    }
  };

  const handleRunBacktestDoub = async () => {
    setBacktestDoubLoading(true);
    setBacktestDoubError('');
    try {
      const res = await apiService.runBacktestFlowDouble(backtestDoubConfig);
      setBacktestDoubResult(res);
      setBacktestDoubTrades(Array.isArray(res?.trades) ? res.trades : []);
    } catch (e) {
      setBacktestDoubError('Failed to run backtest');
      setBacktestDoubResult(null);
      setBacktestDoubTrades([]);
    } finally {
      setBacktestDoubLoading(false);
    }
  };

  const handleExportBacktestTrades = () => {
    if (!Array.isArray(backtestTrades) || backtestTrades.length === 0) return;

    const columns = reorderColumns(
      Object.keys(backtestTrades[0] || {}).filter((c) => !HIDDEN_COLUMNS.has(c)),
    );
    if (columns.length === 0) return;

    const exportColumns = [...columns, 'totalUsdt'];

    const escapeCsv = (v) => {
      const s = v == null ? '' : String(v);
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const header = exportColumns
      .map((c) => escapeCsv(COLUMN_LABELS[c] || c))
      .join(',');
    const lines = backtestTrades.map((row) =>
      exportColumns
        .map((c) => {
          if (c === 'totalUsdt') {
            const tp1 = Number(row?.tp1SizeUsdt);
            const tp2 = Number(row?.tp2SizeUsdt);
            if (Number.isNaN(tp1) && Number.isNaN(tp2)) return escapeCsv('');
            const total = (Number.isNaN(tp1) ? 0 : tp1) + (Number.isNaN(tp2) ? 0 : tp2);
            return escapeCsv(total.toFixed(2));
          }
          return escapeCsv(formatCellValue(c, row?.[c]));
        })
        .join(','),
    );
    const csv = [header, ...lines].join('\r\n');

    const fileNameParts = [
      'backtest_flow',
      backtestResult?.symbol || backtestConfig.symbol || 'symbol',
      backtestResult?.timeFrame || backtestConfig.timeFrame || 'tf',
      new Date().toISOString().slice(0, 10),
    ];
    const fileName = `${fileNameParts.join('_')}.csv`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportBacktestDoubTrades = () => {
    if (!Array.isArray(backtestDoubTrades) || backtestDoubTrades.length === 0) return;

    const columns = reorderColumns(
      Object.keys(backtestDoubTrades[0] || {}).filter((c) => !HIDDEN_COLUMNS.has(c)),
    );
    if (columns.length === 0) return;

    const exportColumns = [...columns, 'totalUsdt'];

    const escapeCsv = (v) => {
      const s = v == null ? '' : String(v);
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const header = exportColumns
      .map((c) => escapeCsv(COLUMN_LABELS[c] || c))
      .join(',');
    const lines = backtestDoubTrades.map((row) =>
      exportColumns
        .map((c) => {
          if (c === 'totalUsdt') {
            const tp1 = Number(row?.tp1SizeUsdt);
            const tp2 = Number(row?.tp2SizeUsdt);
            if (Number.isNaN(tp1) && Number.isNaN(tp2)) return escapeCsv('');
            const total = (Number.isNaN(tp1) ? 0 : tp1) + (Number.isNaN(tp2) ? 0 : tp2);
            return escapeCsv(total.toFixed(2));
          }
          return escapeCsv(formatCellValue(c, row?.[c]));
        })
        .join(','),
    );
    const csv = [header, ...lines].join('\r\n');

    const fileNameParts = [
      'backtest_flowDouble',
      backtestDoubResult?.symbol || backtestDoubConfig.symbol || 'symbol',
      backtestDoubResult?.timeFrame || backtestDoubConfig.timeFrame || 'tf',
      new Date().toISOString().slice(0, 10),
    ];
    const fileName = `${fileNameParts.join('_')}.csv`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Blacklist handlers
  const loadBlacklist = async () => {
    setBlacklistLoading(true);
    setBlacklistError('');
    try {
      const res = await apiService.getBlacklist();
      const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setBlacklistRows(data);
    } catch (e) {
      console.error('Failed to load blacklist', e);
      setBlacklistError('Failed to load blacklist');
      setBlacklistRows([]);
    } finally {
      setBlacklistLoading(false);
    }
  };

  const handleAddBlacklist = async () => {
    if (!blacklistSymbol.trim()) {
      setBlacklistError('Symbol is required');
      return;
    }
    setBlacklistLoading(true);
    setBlacklistError('');
    try {
      await apiService.addBlacklist({
        symbol: blacklistSymbol.trim().toUpperCase(),
        reason: blacklistReason.trim(),
      });
      setBlacklistSymbol('');
      setBlacklistReason('');
      await loadBlacklist();
    } catch (e) {
      console.error('Failed to add blacklist', e);
      setBlacklistError('Failed to add symbol to blacklist');
    } finally {
      setBlacklistLoading(false);
    }
  };

  const handleRemoveBlacklist = async (symbol) => {
    if (!symbol) return;
    setBlacklistLoading(true);
    setBlacklistError('');
    try {
      await apiService.removeBlacklist(symbol);
      await loadBlacklist();
    } catch (e) {
      console.error('Failed to remove blacklist', e);
      setBlacklistError('Failed to remove symbol from blacklist');
    } finally {
      setBlacklistLoading(false);
    }
  };

  // Symbol info handlers
  const handleLoadSymbolInfo = async () => {
    if (!symbolInfoSymbol.trim()) {
      setSymbolInfoError('Symbol is required');
      return;
    }
    setSymbolInfoLoading(true);
    setSymbolInfoError('');
    setSymbolInfoData(null);
    try {
      const res = await apiService.getSymbolInfo(symbolInfoSymbol.trim().toUpperCase());
      const data = res && res.data ? res.data : res;
      setSymbolInfoData(data);
    } catch (e) {
      console.error('Failed to load symbol info', e);
      setSymbolInfoError('Failed to load symbol info');
      setSymbolInfoData(null);
    } finally {
      setSymbolInfoLoading(false);
    }
  };

  const handleLoadSymbolMarketData = async () => {
    if (!symbolInfoSymbol.trim()) {
      setSymbolMarketError('Symbol is required');
      return;
    }
    setSymbolMarketLoading(true);
    setSymbolMarketError('');
    setSymbolMarketData(null);
    try {
      const res = await apiService.getSymbolMarketData(symbolInfoSymbol.trim().toUpperCase());
      const data = res && res.data ? res.data : res;
      setSymbolMarketData(data);
    } catch (e) {
      console.error('Failed to load market data', e);
      setSymbolMarketError('Failed to load market data');
      setSymbolMarketData(null);
    } finally {
      setSymbolMarketLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
        {/* Shared snapshot inputs */}
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="flex flex-col">
            <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
              Date
            </label>
            <input
              type="date"
              value={snapshotDate}
              onChange={(e) => setSnapshotDate(e.target.value)}
              className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
              Hour (0–23)
            </label>
            <select
              value={snapshotHour}
              onChange={(e) => setSnapshotHour(e.target.value)}
              className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
            >
              <option value="">Select hour</option>
              {hourOptions.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleLoadSnapshots}
            className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600"
          >
            Load snapshots
          </button>
          <button
            onClick={() => {
              setSnapshotDate('');
              setSnapshotHour('');
              setSnapshotError('');
              setTrendingRows([]);
              setTrendingError('');
              setScreenerRows([]);
              setScreenerError('');
              setCurrentTrendingRows([]);
              setCurrentTrendingError('');
              setDeletedTrendingRows([]);
              setDeletedTrendingError('');
              setTrendingSymbolFilter('');
              setTrendingMinQuoteVol('');
              setTrendingMinPctChange('');
              setTrendingMinOpenInterest('');
              setTrendingMaxSpreadBps('');
              setTrendingMinFundingRate('');
              setTrendingMinScore('');
              setTrendingSortColumn('score');
              setTrendingSortDirection('desc');
              setScreenerSymbolFilter('');
              setScreenerMinQuoteVol('');
              setScreenerMinPctChange('');
              setScreenerMinOpenInterest('');
              setScreenerMaxSpreadBps('');
              setScreenerMinFundingRate('');
              setScreenerMinScore('');
              setScreenerSortColumn('score');
              setScreenerSortDirection('desc');
            }}
            className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600"
          >
            Clear
          </button>
        </div>
        {snapshotError && (
          <div className="text-xs sm:text-sm text-red-400 mb-3">{snapshotError}</div>
        )}

        {/* Tabs */}
        <div className="flex gap-3 border-b border-dark-700 mb-4 overflow-x-auto">
          <button
            onClick={() => setActiveTab('trending')}
            className={`px-3 py-2 rounded-t ${
              activeTab === 'trending'
                ? 'bg-dark-700 text-white'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            TrendingSymbolScreener
          </button>
          <button
            onClick={() => setActiveTab('screener')}
            className={`px-3 py-2 rounded-t ${
              activeTab === 'screener'
                ? 'bg-dark-700 text-white'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            SymbolScreener
          </button>
          <button
            onClick={() => setActiveTab('currentTrending')}
            className={`px-3 py-2 rounded-t ${
              activeTab === 'currentTrending'
                ? 'bg-dark-700 text-white'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            currentTrending
          </button>
          <button
            onClick={() => setActiveTab('deletedTrending')}
            className={`px-3 py-2 rounded-t ${
              activeTab === 'deletedTrending'
                ? 'bg-dark-700 text-white'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            deletedTrending
          </button>
          <button
            onClick={() => setActiveTab('backtest')}
            className={`px-3 py-2 rounded-t ${
              activeTab === 'backtest'
                ? 'bg-dark-700 text-white'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            Back Test FIB
          </button>
          <button
            onClick={() => setActiveTab('backtestDoub')}
            className={`px-3 py-2 rounded-t ${
              activeTab === 'backtestDoub'
                ? 'bg-dark-700 text-white'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            Back Test DOUB
          </button>
          <button
            onClick={() => setActiveTab('blacklisted')}
            className={`px-3 py-2 rounded-t ${
              activeTab === 'blacklisted'
                ? 'bg-dark-700 text-white'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            Blacklisted Symbol
          </button>
          <button
            onClick={() => setActiveTab('symbolInfo')}
            className={`px-3 py-2 rounded-t ${
              activeTab === 'symbolInfo'
                ? 'bg-dark-700 text-white'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            Symbol Info
          </button>
        </div>

        {activeTab === 'trending' && (
          <div className="space-y-4">
            {/* Filters & sorting for trending data */}
            <div className="flex flex-wrap items-end gap-3 mb-2">
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Symbol contains
                </label>
                <input
                  type="text"
                  value={trendingSymbolFilter}
                  onChange={(e) => setTrendingSymbolFilter(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-xs sm:text-sm"
                  placeholder="e.g. BTC"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Sort by
                </label>
                <select
                  value={trendingSortColumn}
                  onChange={(e) => setTrendingSortColumn(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-xs sm:text-sm"
                >
                  <option value="">None</option>
                  <option value="id">id</option>
                  <option value="runTime">runTime</option>
                  <option value="symbol">symbol</option>
                  <option value="quoteVol">quoteVol</option>
                  <option value="pctChange">pctChange</option>
                  <option value="openInterest">openInterest</option>
                  <option value="spreadBps">spreadBps</option>
                  <option value="fundingRate">fundingRate</option>
                  <option value="score">score</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Direction
                </label>
                <select
                  value={trendingSortDirection}
                  onChange={(e) => setTrendingSortDirection(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-xs sm:text-sm"
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
              </div>
            </div>
            {trendingError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">{trendingError}</div>
            )}
            {trendingLoading ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                Loading trending symbol snapshot...
              </div>
            ) : filterAndSortRows(trendingRows, {
              symbolFilter: trendingSymbolFilter,
              minQuoteVol: trendingMinQuoteVol,
              minPctChange: trendingMinPctChange,
              minOpenInterest: trendingMinOpenInterest,
              maxSpreadBps: trendingMaxSpreadBps,
              minFundingRate: trendingMinFundingRate,
              minScore: trendingMinScore,
              sortColumn: trendingSortColumn,
              sortDirection: trendingSortDirection,
            }).length === 0 ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                No data. Choose date & hour and click &quot;Load snapshot&quot;.
              </div>
            ) : (
              renderGenericTable(
                filterAndSortRows(trendingRows, {
                  symbolFilter: trendingSymbolFilter,
                  minQuoteVol: trendingMinQuoteVol,
                  minPctChange: trendingMinPctChange,
                  minOpenInterest: trendingMinOpenInterest,
                  maxSpreadBps: trendingMaxSpreadBps,
                  minFundingRate: trendingMinFundingRate,
                  minScore: trendingMinScore,
                  sortColumn: trendingSortColumn,
                  sortDirection: trendingSortDirection,
                }),
              )
            )}
          </div>
        )}

        {activeTab === 'screener' && (
          <div className="space-y-4">
            {/* Filters & sorting for screener data */}
            <div className="flex flex-wrap items-end gap-3 mb-2">
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Symbol contains
                </label>
                <input
                  type="text"
                  value={screenerSymbolFilter}
                  onChange={(e) => setScreenerSymbolFilter(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-xs sm:text-sm"
                  placeholder="e.g. BTC"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Sort by
                </label>
                <select
                  value={screenerSortColumn}
                  onChange={(e) => setScreenerSortColumn(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-xs sm:text-sm"
                >
                  <option value="">None</option>
                  <option value="id">id</option>
                  <option value="runTime">runTime</option>
                  <option value="symbol">symbol</option>
                  <option value="quoteVol">quoteVol</option>
                  <option value="pctChange">pctChange</option>
                  <option value="openInterest">openInterest</option>
                  <option value="spreadBps">spreadBps</option>
                  <option value="fundingRate">fundingRate</option>
                  <option value="score">score</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Direction
                </label>
                <select
                  value={screenerSortDirection}
                  onChange={(e) => setScreenerSortDirection(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-xs sm:text-sm"
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
              </div>
            </div>
            {screenerError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">{screenerError}</div>
            )}
            {screenerLoading ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                Loading symbol screener snapshot...
              </div>
            ) : filterAndSortRows(screenerRows, {
              symbolFilter: screenerSymbolFilter,
              minQuoteVol: screenerMinQuoteVol,
              minPctChange: screenerMinPctChange,
              minOpenInterest: screenerMinOpenInterest,
              maxSpreadBps: screenerMaxSpreadBps,
              minFundingRate: screenerMinFundingRate,
              minScore: screenerMinScore,
              sortColumn: screenerSortColumn,
              sortDirection: screenerSortDirection,
            }).length === 0 ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                No data. Choose date & hour and click &quot;Load snapshot&quot;.
              </div>
            ) : (
              renderGenericTable(
                filterAndSortRows(screenerRows, {
                  symbolFilter: screenerSymbolFilter,
                  minQuoteVol: screenerMinQuoteVol,
                  minPctChange: screenerMinPctChange,
                  minOpenInterest: screenerMinOpenInterest,
                  maxSpreadBps: screenerMaxSpreadBps,
                  minFundingRate: screenerMinFundingRate,
                  minScore: screenerMinScore,
                  sortColumn: screenerSortColumn,
                  sortDirection: screenerSortDirection,
                }),
              )
            )}
          </div>
        )}

        {activeTab === 'currentTrending' && (
          <div className="space-y-4">
            <div className="text-sm sm:text-base text-dark-300">
              Current trending symbols for the selected date.
            </div>
            {currentTrendingRows.length > 0 && (
              <div className="text-xs sm:text-sm text-dark-300">
                Total count:{' '}
                <span className="text-white font-semibold">{currentTrendingRows.length}</span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <button
                onClick={handleLoadCurrentTrending}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600"
              >
                Load currentTrending
              </button>
              <button
                onClick={handleRerankCurrentTrending}
                disabled={currentTrendingRanking || currentTrendingLoading}
                className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
              >
                {currentTrendingRanking ? 'Re-ranking...' : 'Re-Rank'}
              </button>
              <button
                onClick={() => {
                  setCurrentTrendingRows([]);
                  setCurrentTrendingError('');
                }}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600"
              >
                Clear
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs sm:text-sm text-dark-300 font-medium">Sort by:</span>
              {[
                { key: 'currentTrendingSymbolId', label: 'ID' },
                { key: 'symbol', label: 'Symbol' },
                { key: 'startTime', label: 'Start Time' },
                { key: 'endTime', label: 'End Time' },
                { key: 'upOrDown', label: 'Up/Down' },
                { key: 'rank', label: 'Rank' },
                { key: 'volatilityScore', label: 'Volatility' },
              ].map((btn) => {
                const isActive = currentTrendingSortColumn === btn.key;
                const dir = currentTrendingSortDirection === 'asc' ? '↑' : '↓';
                return (
                  <button
                    key={btn.key}
                    type="button"
                    onClick={() => handleCurrentTrendingSort(btn.key)}
                    className={`px-3 py-1 rounded-md text-xs sm:text-sm border transition-colors ${
                      isActive
                        ? 'bg-primary-600 text-white border-primary-500'
                        : 'bg-dark-700 text-dark-200 border-dark-600 hover:bg-dark-600'
                    }`}
                  >
                    {btn.label} {isActive ? dir : ''}
                  </button>
                );
              })}
            </div>
            {currentTrendingError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">
                {currentTrendingError}
              </div>
            )}
            {currentTrendingLoading ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                Loading current trending data...
              </div>
            ) : currentTrendingRows.length === 0 ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                No data. Select a date above and click &quot;Load currentTrending&quot;.
              </div>
            ) : (
              renderGenericTable(
                sortCurrentTrendingRows(currentTrendingRows).map((row) => {
                  const orderedRow = {
                    rank: row.rank,
                    currentTrendingSymbolId: row.currentTrendingSymbolId,
                    symbol: row.symbol,
                    startTime: row.startTime,
                    endTime: row.endTime,
                    upOrDown: row.upOrDown,
                    volatilityScore: row.volatilityScore,
                  };
                  return {
                    ...orderedRow,
                  actions: (
                    <button
                      type="button"
                      onClick={() => handleDeleteCurrentTrending(row.symbol)}
                      disabled={currentTrendingDeleting}
                      className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                    >
                      {currentTrendingDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  ),
                  };
                }),
              )
            )}
          </div>
        )}

        {activeTab === 'deletedTrending' && (
          <div className="space-y-4">
            <div className="text-sm sm:text-base text-dark-300">
              Deleted trending symbols for the selected date.
            </div>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={deletedDate}
                  onChange={(e) => setDeletedDate(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <button
                onClick={handleLoadDeletedTrending}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600"
              >
                Load deletedTrending
              </button>
              <button
                onClick={() => {
                  setDeletedTrendingRows([]);
                  setDeletedTrendingError('');
                  setDeletedDate('');
                }}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600"
              >
                Clear
              </button>
            </div>
            {deletedTrendingError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">
                {deletedTrendingError}
              </div>
            )}
            {deletedTrendingLoading ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                Loading deleted trending data...
              </div>
            ) : deletedTrendingRows.length === 0 ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                No data. Select a date above and click &quot;Load deletedTrending&quot;.
              </div>
            ) : (
              renderGenericTable(deletedTrendingRows)
            )}
          </div>
        )}

        {activeTab === 'backtest' && (
          <div className="space-y-4">
            <div className="text-sm sm:text-base text-dark-300">
              Run a signal-receiver flow backtest.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Symbol
                </label>
                <input
                  type="text"
                  name="symbol"
                  value={backtestConfig.symbol}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  placeholder="e.g. BTCUSDT"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Days back
                </label>
                <input
                  type="number"
                  name="daysBack"
                  value={backtestConfig.daysBack}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  min="1"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Time frame
                </label>
                <select
                  name="timeFrame"
                  value={backtestConfig.timeFrame}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                >
                  <option value="1m">1m</option>
                  <option value="3m">3m</option>
                  <option value="5m">5m</option>
                  <option value="15m">15m</option>
                  <option value="30m">30m</option>
                  <option value="1h">1h</option>
                  <option value="2h">2h</option>
                  <option value="4h">4h</option>
                  <option value="6h">6h</option>
                  <option value="8h">8h</option>
                  <option value="12h">12h</option>
                  <option value="1d">1d</option>
                  <option value="3d">3d</option>
                  <option value="1w">1w</option>
                  <option value="1M">1M</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  ADX period
                </label>
                <input
                  type="number"
                  name="samplingPeriod"
                  value={backtestConfig.samplingPeriod}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  min="1"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  RSI Index
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="rangeMultiplier"
                  value={backtestConfig.rangeMultiplier}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Initial size (USDT)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="initialSizeUsdt"
                  value={backtestConfig.initialSizeUsdt}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  TP1 (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="tp1Percent"
                  value={backtestConfig.tp1Percent}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  TP2 (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="tp2Percent"
                  value={backtestConfig.tp2Percent}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  SL (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="slPercent"
                  value={backtestConfig.slPercent}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Wait Failures
                </label>
                <input
                  type="number"
                  name="waitFailures"
                  value={backtestConfig.waitFailures}
                  onChange={handleBacktestInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  min="0"
                  step="1"
                />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  name="verbose"
                  checked={backtestConfig.verbose}
                  onChange={handleBacktestInputChange}
                  className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                />
                <label className="text-dark-300 text-xs sm:text-sm font-medium">
                  Verbose results
                </label>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleRunBacktest}
                disabled={backtestLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {backtestLoading ? 'Running backtest...' : 'Run Back Test'}
              </button>
              <button
                type="button"
                onClick={handleExportBacktestTrades}
                disabled={!backtestTrades || backtestTrades.length === 0}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Export
              </button>
              <button
                onClick={() => {
                  setBacktestResult(null);
                  setBacktestTrades([]);
                  setBacktestError('');
                }}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600"
              >
                Clear results
              </button>
            </div>

            {backtestError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">
                {backtestError}
              </div>
            )}

            {backtestResult && (() => {
              const slPct = Number(backtestResult.slPercent ?? 0);
              const leverage = slPct > 0 ? 100 / slPct : null;
              const maxMarginUsed =
                leverage && backtestResult.maxPositionSizeUsdt != null
                  ? backtestResult.maxPositionSizeUsdt / leverage
                  : null;
              const totalUsdtRequired =
                maxMarginUsed != null && backtestResult.maxDrawdownUsdt != null
                  ? maxMarginUsed + backtestResult.maxDrawdownUsdt
                  : null;

              return (
                <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg space-y-3">
                  <h4 className="text-sm sm:text-base font-semibold text-white">
                    Summary
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm text-dark-200">
                    <div>
                      <div className="text-dark-400">Symbol / Timeframe</div>
                      <div className="text-white font-medium">
                        {backtestResult.symbol} / {backtestResult.timeFrame}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Final PnL (USDT)</div>
                      <div className={backtestResult.finalPnlUsdt >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {backtestResult.finalPnlUsdt?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Max Drawdown (USDT)</div>
                      <div className="text-yellow-400">
                        {backtestResult.maxDrawdownUsdt?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Max Lot Multiplier</div>
                      <div className="text-white">
                        {backtestResult.maxLotMultiplier?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Max Position Size (USDT)</div>
                      <div className="text-white">
                        {backtestResult.maxPositionSizeUsdt?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Leverage (approx)</div>
                      <div className="text-white">
                        {leverage ? leverage.toFixed(2) : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Max Margin Used (USDT)</div>
                      <div className="text-white">
                        {maxMarginUsed != null ? maxMarginUsed.toFixed(2) : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Total USDT Required</div>
                      <div className="text-white">
                        {totalUsdtRequired != null ? totalUsdtRequired.toFixed(2) : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {backtestTrades.length > 0 && (
              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
                <h4 className="text-sm sm:text-base font-semibold text-white mb-3">
                  Trades
                </h4>
                {renderGenericTable(backtestTrades)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'backtestDoub' && (
          <div className="space-y-4">
            <div className="text-sm sm:text-base text-dark-300">
              Run a signal-receiver flow backtest (doubling strategy).
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Symbol
                </label>
                <input
                  type="text"
                  name="symbol"
                  value={backtestDoubConfig.symbol}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  placeholder="e.g. BTCUSDT"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Days back
                </label>
                <input
                  type="number"
                  name="daysBack"
                  value={backtestDoubConfig.daysBack}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  min="1"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Time frame
                </label>
                <select
                  name="timeFrame"
                  value={backtestDoubConfig.timeFrame}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                >
                  <option value="1m">1m</option>
                  <option value="3m">3m</option>
                  <option value="5m">5m</option>
                  <option value="15m">15m</option>
                  <option value="30m">30m</option>
                  <option value="1h">1h</option>
                  <option value="2h">2h</option>
                  <option value="4h">4h</option>
                  <option value="6h">6h</option>
                  <option value="8h">8h</option>
                  <option value="12h">12h</option>
                  <option value="1d">1d</option>
                  <option value="3d">3d</option>
                  <option value="1w">1w</option>
                  <option value="1M">1M</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  ADX period
                </label>
                <input
                  type="number"
                  name="samplingPeriod"
                  value={backtestDoubConfig.samplingPeriod}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  min="1"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  RSI Index
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="rangeMultiplier"
                  value={backtestDoubConfig.rangeMultiplier}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Initial size (USDT)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="initialSizeUsdt"
                  value={backtestDoubConfig.initialSizeUsdt}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  TP1 (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="tp1Percent"
                  value={backtestDoubConfig.tp1Percent}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  TP2 (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="tp2Percent"
                  value={backtestDoubConfig.tp2Percent}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  SL (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="slPercent"
                  value={backtestDoubConfig.slPercent}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Wait Failures
                </label>
                <input
                  type="number"
                  name="waitFailures"
                  value={backtestDoubConfig.waitFailures}
                  onChange={handleBacktestDoubInputChange}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  min="0"
                  step="1"
                />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  name="verbose"
                  checked={backtestDoubConfig.verbose}
                  onChange={handleBacktestDoubInputChange}
                  className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                />
                <label className="text-dark-300 text-xs sm:text-sm font-medium">
                  Verbose results
                </label>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleRunBacktestDoub}
                disabled={backtestDoubLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {backtestDoubLoading ? 'Running backtest...' : 'Run Back Test'}
              </button>
              <button
                type="button"
                onClick={handleExportBacktestDoubTrades}
                disabled={!backtestDoubTrades || backtestDoubTrades.length === 0}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Export
              </button>
              <button
                onClick={() => {
                  setBacktestDoubResult(null);
                  setBacktestDoubTrades([]);
                  setBacktestDoubError('');
                }}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600"
              >
                Clear results
              </button>
            </div>

            {backtestDoubError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">
                {backtestDoubError}
              </div>
            )}

            {backtestDoubResult && (() => {
              const slPct = Number(backtestDoubResult.slPercent ?? 0);
              const leverage = slPct > 0 ? 100 / slPct : null;
              const maxMarginUsed =
                leverage && backtestDoubResult.maxPositionSizeUsdt != null
                  ? backtestDoubResult.maxPositionSizeUsdt / leverage
                  : null;
              const totalUsdtRequired =
                maxMarginUsed != null && backtestDoubResult.maxDrawdownUsdt != null
                  ? maxMarginUsed + backtestDoubResult.maxDrawdownUsdt
                  : null;

              return (
                <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg space-y-3">
                  <h4 className="text-sm sm:text-base font-semibold text-white">
                    Summary
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm text-dark-200">
                    <div>
                      <div className="text-dark-400">Symbol / Timeframe</div>
                      <div className="text-white font-medium">
                        {backtestDoubResult.symbol} / {backtestDoubResult.timeFrame}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Final PnL (USDT)</div>
                      <div className={backtestDoubResult.finalPnlUsdt >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {backtestDoubResult.finalPnlUsdt?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Max Drawdown (USDT)</div>
                      <div className="text-yellow-400">
                        {backtestDoubResult.maxDrawdownUsdt?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Max Lot Multiplier</div>
                      <div className="text-white">
                        {backtestDoubResult.maxLotMultiplier?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Max Position Size (USDT)</div>
                      <div className="text-white">
                        {backtestDoubResult.maxPositionSizeUsdt?.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Leverage (approx)</div>
                      <div className="text-white">
                        {leverage ? leverage.toFixed(2) : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Max Margin Used (USDT)</div>
                      <div className="text-white">
                        {maxMarginUsed != null ? maxMarginUsed.toFixed(2) : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-dark-400">Total USDT Required</div>
                      <div className="text-white">
                        {totalUsdtRequired != null ? totalUsdtRequired.toFixed(2) : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {backtestDoubTrades.length > 0 && (
              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
                <h4 className="text-sm sm:text-base font-semibold text-white mb-3">
                  Trades
                </h4>
                {renderGenericTable(backtestDoubTrades)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'blacklisted' && (
          <div className="space-y-4">
            <div className="text-sm sm:text-base text-dark-300">
              Manage blacklisted symbols. Add, remove, and view the current blacklist.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Symbol
                </label>
                <input
                  type="text"
                  value={blacklistSymbol}
                  onChange={(e) => setBlacklistSymbol(e.target.value.toUpperCase())}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  placeholder="e.g. BTCUSDT"
                />
              </div>
              <div className="flex flex-col sm:col-span-2">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={blacklistReason}
                  onChange={(e) => setBlacklistReason(e.target.value)}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  placeholder="e.g. High volatility"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleAddBlacklist}
                disabled={blacklistLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {blacklistLoading ? 'Adding...' : 'Add to Blacklist'}
              </button>
              <button
                onClick={loadBlacklist}
                disabled={blacklistLoading}
                className="px-4 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm font-medium hover:bg-dark-600 disabled:opacity-50"
              >
                {blacklistLoading ? 'Loading...' : 'Load Blacklist'}
              </button>
              <button
                onClick={() => {
                  setBlacklistSymbol('');
                  setBlacklistReason('');
                  setBlacklistError('');
                  setBlacklistRows([]);
                }}
                className="px-4 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm font-medium hover:bg-dark-600"
              >
                Clear
              </button>
            </div>

            {blacklistError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">
                {blacklistError}
              </div>
            )}

            {blacklistLoading ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                Loading blacklist...
              </div>
            ) : blacklistRows.length === 0 ? (
              <div className="p-6 text-center text-dark-400 text-sm bg-dark-800 border border-dark-700 rounded-xl">
                No blacklisted symbols.
              </div>
            ) : (
              <div className="space-y-3">
                {renderGenericTable(
                  blacklistRows.map((row) => ({
                    ...row,
                    actions: (
                      <button
                        type="button"
                        onClick={() => handleRemoveBlacklist(row.symbol)}
                        className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                      >
                        Remove
                      </button>
                    ),
                  }))
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'symbolInfo' && (
          <div className="space-y-4">
            <div className="text-sm sm:text-base text-dark-300">
              Lookup live symbol information from Binance backend.
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col">
                <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">
                  Symbol
                </label>
                <input
                  type="text"
                  value={symbolInfoSymbol}
                  onChange={(e) => setSymbolInfoSymbol(e.target.value.toUpperCase())}
                  className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                  placeholder="e.g. BTCUSDT"
                />
              </div>
              <button
                onClick={handleLoadSymbolInfo}
                disabled={symbolInfoLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {symbolInfoLoading ? 'Loading...' : 'Load Symbol Info'}
              </button>
              <button
                onClick={handleLoadSymbolMarketData}
                disabled={symbolMarketLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {symbolMarketLoading ? 'Loading...' : 'Load Market Data'}
              </button>
              <button
                onClick={() => {
                  setSymbolInfoSymbol('');
                  setSymbolInfoData(null);
                  setSymbolInfoError('');
                  setSymbolMarketData(null);
                  setSymbolMarketError('');
                }}
                className="px-4 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm font-medium hover:bg-dark-600"
              >
                Clear
              </button>
            </div>

            {symbolInfoError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">
                {symbolInfoError}
              </div>
            )}
            {symbolMarketError && (
              <div className="text-xs sm:text-sm text-red-400 mb-2">
                {symbolMarketError}
              </div>
            )}

            {symbolInfoLoading ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                Loading symbol info...
              </div>
            ) : !symbolInfoData ? (
              <div className="p-6 text-center text-dark-400 text-sm bg-dark-800 border border-dark-700 rounded-xl">
                No data. Enter a symbol and click &quot;Load Symbol Info&quot;.
              </div>
            ) : (
              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
                <h4 className="text-sm sm:text-base font-semibold text-white mb-3">
                  Symbol Info: {symbolInfoSymbol || ''}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white w-1/3">
                          Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(symbolInfoData).map(([key, val]) => (
                        <tr key={key} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-xs sm:text-sm text-dark-300">{key}</td>
                          <td className="px-4 py-3 text-xs sm:text-sm text-white">
                            {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {symbolMarketLoading ? (
              <div className="p-6 text-center text-dark-400 text-sm">
                Loading market data...
              </div>
            ) : symbolMarketData ? (
              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
                <h4 className="text-sm sm:text-base font-semibold text-white mb-3">
                  Market Data: {symbolInfoSymbol || ''}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white w-1/3">
                          Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(symbolMarketData).map(([key, val]) => (
                        <tr key={key} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-xs sm:text-sm text-dark-300">{key}</td>
                          <td className="px-4 py-3 text-xs sm:text-sm text-white">
                            {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default Technicals;
